import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PATH } from "../redux/utils";
import { MODULO_KEYS, ACTION_KEYS } from "../constants/modulos";
import { PERMISO_SOLICITAR_CAMBIOS } from "../utils/permisosEspeciales";

const PermisosContext = createContext(null);

const buildDefault = () =>
  Object.fromEntries(
    MODULO_KEYS.map((m) => [m, { ...Object.fromEntries(ACTION_KEYS.map((a) => [a, true])), ciudades: null }])
  );

export function PermisosProvider({ children }) {
  const [permisos, setPermisos] = useState(buildDefault());
  const [loaded, setLoaded] = useState(false);

  // `silent`: sondeos periodicos; si fallan por red se conservan los permisos vigentes
  // (solo se vacian si el servidor rechaza la sesion).
  const fetchPermisos = useCallback(async (silent = false) => {
    const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
    if (!token) {
      setPermisos(buildDefault());
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${PATH}/users/me/permisos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = data || buildDefault();
      // Solo actualiza si cambio algo, para no re-renderizar en cada sondeo.
      setPermisos((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    } catch (err) {
      const status = err?.response?.status;
      if (silent !== true || status === 401 || status === 403) setPermisos(buildDefault());
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchPermisos();
  }, [fetchPermisos]);

  useEffect(() => {
    const handler = () => fetchPermisos();
    window.addEventListener("permisos-refresh", handler);
    // El admin puede cambiar permisos (p. ej. "Solicitar cambio") desde otra sesion:
    // se sondea cada pocos segundos y al volver a la pestana.
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchPermisos(true);
    }, 5000);
    const onVisible = () => { if (document.visibilityState === "visible") fetchPermisos(true); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.removeEventListener("permisos-refresh", handler);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [fetchPermisos]);

  const perm = (module, action) => Boolean(permisos?.[module]?.[action]);

  // Ciudades permitidas para el usuario en ese modulo: null = todas, array = solo esas
  // (array vacio = ninguna). Ver columna "Ciudades" en el detalle de usuario > Permisos.
  const allowedCities = (module) => {
    const ciudades = permisos?.[module]?.ciudades;
    return Array.isArray(ciudades) ? ciudades : null;
  };

  const canUseCity = (module, idCiudad) => {
    const allowed = allowedCities(module);
    if (allowed === null) return true;
    if (idCiudad === null || idCiudad === undefined) return false;
    return allowed.some((id) => String(id) === String(idCiudad));
  };

  return (
    <PermisosContext.Provider value={{ permisos, perm, allowedCities, canUseCity, loaded, refresh: fetchPermisos }}>
      {children}
    </PermisosContext.Provider>
  );
}

export const usePermisos = () => useContext(PermisosContext);

// true si el usuario en sesion puede enviar "Solicitar cambio". El backend
// (GET /users/me/permisos) ya devuelve true para administracion; se exige
// === true porque el valor por defecto de este contexto es permisivo.
export const usePuedeSolicitarCambios = () => {
  const ctx = usePermisos();
  return ctx?.permisos?.[PERMISO_SOLICITAR_CAMBIOS] === true;
};

// true si el usuario en sesion es administracion (ADMIN_USER_IDS en el backend).
// Se usa para mostrar controles exclusivos de admin, p. ej. el filtro de
// usuario en el centro de notificaciones.
export const useEsAdministracion = () => {
  const ctx = usePermisos();
  return ctx?.permisos?.es_administracion === true;
};
